import {
  useReactFlow,
  useStoreApi,
  type FitViewOptions,
  type Rect,
  type XYPosition,
} from '@xyflow/react';
import { useCallback } from 'react';

import { ReadableViewportHelper } from './readable-viewport-helper';

interface UseReadableFitViewParams {
  nodes: ReadonlyArray<{ position: XYPosition }>;
  nodeSize: { width: number; height: number };
  padding?: FitViewOptions['padding'];
  minZoom: number;
  contentBounds?: Rect;
}

// Must be called inside <ReactFlowProvider>. Replacement for rf.fitView that
// never shrinks the chart below minZoom.
function useReadableFitView({
  nodes,
  nodeSize,
  padding,
  minZoom,
  contentBounds,
}: Readonly<UseReadableFitViewParams>) {
  const rf = useReactFlow();
  const store = useStoreApi();
  const { width: nodeWidth, height: nodeHeight } = nodeSize;

  return useCallback(
    (options?: { duration?: number }) => {
      const { width, height } = store.getState();
      const viewport = ReadableViewportHelper.getViewport({
        nodePositions: nodes.map((n) => n.position),
        nodeSize: { width: nodeWidth, height: nodeHeight },
        canvasSize: { width, height },
        padding,
        minZoom,
        contentBounds,
      });
      if (viewport) rf.setViewport(viewport, options);
    },
    [rf, store, nodes, nodeWidth, nodeHeight, padding, minZoom, contentBounds]
  );
}

export { useReadableFitView };
